import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, ShieldCheck, Users, Sparkles, CalendarDays, ChevronDown, Mail, Phone, MessageCircle } from "lucide-react";


type ProfileRow = {
  id: string;
  user_id: string | null;
  nombre: string | null;
  apellidos: string | null;
  ciudad: string | null;
  status: string | null;
  created_at: string | null;
};

type UserProfileRow = {
  id: string;
  user_id: string;
  display_name: string | null;
  phone: string | null;
  created_at: string | null;
};

type BookingRow = {
  id: string;
  client_name: string | null;
  client_email: string | null;
  appointment_date: string | null;
  appointment_time: string | null;
  status: string | null;
  amount_cents: number | null;
};

const StatCard = ({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: number | string }) => (
  <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-primary">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="font-serif text-2xl text-foreground">{value}</p>
      </div>
    </div>
  </div>
);

type Application = {
  id: string;
  user_id: string | null;
  nombre: string | null;
  apellidos: string | null;
  email: string | null;
  telefono: string | null;
  whatsapp: string | null;
  ciudad: string | null;
  direccion: string | null;
  bio: string | null;
  descripcion: string | null;
  idiomas: string[] | null;
  tipos_masaje: string[] | null;
  anos_experiencia: number | null;
  certificaciones: string[] | null;
  precio_desde: number | null;
  precio_hasta: number | null;
  foto_portada: string | null;
  galeria_fotos: string[] | null;
  es_centro_masajes: boolean | null;
  nombre_centro: string | null;
  status: string | null;
  is_demo: boolean | null;
  created_at: string | null;
  ultima_nota: string | null;
  ultima_revision: string | null;
};

type ReviewHistoryRow = {
  decision: string | null;
  status_anterior: string | null;
  notas: string | null;
  created_at: string | null;
};

const ESTADOS = [
  { value: "pendiente", label: "Pendientes" },
  { value: "aprobado", label: "Aprobados" },
  { value: "rechazado", label: "Rechazados" },
  { value: "suspendido", label: "Suspendidos" },
  { value: "todos", label: "Todos" },
];

const formatoFecha = (valor: string | null) => {
  if (!valor) return "";
  try {
    return new Date(valor).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

const formatoFechaHora = (valor: string | null) => {
  if (!valor) return "";
  try {
    return new Date(valor).toLocaleString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const etiquetaTipo = (valor: string) => {
  const texto = valor.replace(/_/g, " ").trim();
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

const mensajeError = (error: unknown) => {
  if (error && typeof error === "object" && "message" in error) {
    const msg = String((error as { message?: string }).message ?? "");
    if (msg.trim()) return msg;
  }
  return "No se ha podido completar la acción";
};

const ReviewHistory = ({ profileId }: { profileId: string }) => {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ReviewHistoryRow[]>([]);

  const handleOpenChange = async (next: boolean) => {
    setOpen(next);
    if (!next || loaded) return;
    setLoading(true);
    const { data, error } = await supabase.rpc("admin_therapist_review_history", { p_profile_id: profileId });
    if (error) {
      toast.error(mensajeError(error));
    } else {
      setRows((data as ReviewHistoryRow[]) ?? []);
      setLoaded(true);
    }
    setLoading(false);
  };

  return (
    <Collapsible open={open} onOpenChange={handleOpenChange}>
      <CollapsibleTrigger className="flex items-center gap-2 text-sm text-primary underline-offset-4 hover:underline">
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        Historial de revisiones
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3 space-y-2">
        {loading && <Skeleton className="h-16 w-full rounded-xl" />}
        {!loading && loaded && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">Todavía no hay revisiones registradas.</p>
        )}
        {!loading &&
          rows.map((r, i) => (
            <div key={`${profileId}-${i}`} className="rounded-xl border border-border bg-secondary/40 p-3 text-sm">
              <p className="font-medium text-foreground">
                {r.decision ?? "sin decisión"}
                {r.status_anterior ? ` (antes: ${r.status_anterior})` : ""}
              </p>
              {r.notas && <p className="mt-1 text-muted-foreground">{r.notas}</p>}
              <p className="mt-1 text-xs text-muted-foreground">{formatoFechaHora(r.created_at)}</p>
            </div>
          ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

const ApplicationCard = ({
  app,
  onDecision,
}: {
  app: Application;
  onDecision: (id: string, decision: string, notas: string) => Promise<boolean>;
}) => {
  const [detalleAbierto, setDetalleAbierto] = useState(false);
  const [dialogo, setDialogo] = useState<null | "aprobado" | "rechazado" | "suspendido">(null);
  const [notas, setNotas] = useState("");
  const [enviando, setEnviando] = useState(false);

  const nombreCompleto = [app.nombre, app.apellidos].filter(Boolean).join(" ") || "Sin nombre";
  const requiereMotivo = dialogo === "rechazado" || dialogo === "suspendido";

  const abrir = (decision: "aprobado" | "rechazado" | "suspendido") => {
    setNotas("");
    setDialogo(decision);
  };

  const confirmar = async () => {
    if (!dialogo) return;
    if (requiereMotivo && !notas.trim()) return;
    setEnviando(true);
    const ok = await onDecision(app.id, dialogo, notas.trim());
    setEnviando(false);
    if (ok) setDialogo(null);
  };

  return (
    <article className="space-y-4 rounded-2xl border border-border bg-card p-4 shadow-soft sm:p-5">
      <div className="flex flex-col gap-4 sm:flex-row">
        {app.foto_portada && (
          <img
            src={app.foto_portada}
            alt={`Foto de portada de ${nombreCompleto}`}
            width={96}
            height={96}
            loading="lazy"
            className="h-24 w-24 shrink-0 rounded-2xl object-cover"
          />
        )}
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-serif text-xl text-foreground">{nombreCompleto}</h3>
            <Badge variant="secondary">{app.status ?? "sin estado"}</Badge>
            {app.is_demo && <Badge variant="destructive">DEMOSTRACIÓN</Badge>}
          </div>
          <p className="text-sm text-muted-foreground">
            {[app.ciudad, app.created_at ? `Alta el ${formatoFecha(app.created_at)}` : null].filter(Boolean).join(" · ")}
          </p>
          <p className="text-sm text-muted-foreground">
            {[
              app.anos_experiencia != null ? `${app.anos_experiencia} años de experiencia` : null,
              app.precio_desde != null ? `Desde ${app.precio_desde} €` : null,
              app.es_centro_masajes && app.nombre_centro ? `Centro: ${app.nombre_centro}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      </div>

      {(app.tipos_masaje?.length || app.idiomas?.length) && (
        <div className="flex flex-wrap gap-2">
          {(app.tipos_masaje ?? []).map((tipo) => (
            <Badge key={`t-${tipo}`} variant="secondary">{etiquetaTipo(tipo)}</Badge>
          ))}
          {(app.idiomas ?? []).map((idioma) => (
            <Badge key={`i-${idioma}`} variant="outline">{idioma}</Badge>
          ))}
        </div>
      )}

      <div className="grid gap-2 rounded-xl bg-secondary/40 p-3 text-sm">
        <p className="flex items-start gap-2 break-all text-foreground">
          <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          {app.email || "Sin correo"}
        </p>
        <p className="flex items-start gap-2 break-all text-foreground">
          <Phone className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          {app.telefono || "Sin teléfono"}
        </p>
        <p className="flex items-start gap-2 break-all text-foreground">
          <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          {app.whatsapp || "Sin whatsapp"}
        </p>
        {app.direccion && <p className="text-muted-foreground">{app.direccion}</p>}
      </div>

      {(app.bio || app.descripcion) && (
        <Collapsible open={detalleAbierto} onOpenChange={setDetalleAbierto}>
          <CollapsibleTrigger className="flex items-center gap-2 text-sm text-primary underline-offset-4 hover:underline">
            <ChevronDown className={`h-4 w-4 transition-transform ${detalleAbierto ? "rotate-180" : ""}`} aria-hidden="true" />
            Bio y descripción
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3 space-y-3 text-sm text-muted-foreground">
            {app.bio && <p className="whitespace-pre-line">{app.bio}</p>}
            {app.descripcion && <p className="whitespace-pre-line">{app.descripcion}</p>}
            {app.certificaciones?.length ? (
              <p className="text-foreground">Certificaciones: {app.certificaciones.join(", ")}</p>
            ) : null}
          </CollapsibleContent>
        </Collapsible>
      )}

      {(app.ultima_nota || app.ultima_revision) && (
        <div className="rounded-xl border border-dashed border-border p-3 text-sm">
          {app.ultima_nota && <p className="text-foreground">Última nota: {app.ultima_nota}</p>}
          {app.ultima_revision && (
            <p className="text-muted-foreground">Última revisión: {formatoFechaHora(app.ultima_revision)}</p>
          )}
        </div>
      )}

      <ReviewHistory profileId={app.id} />

      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => abrir("aprobado")}>Aprobar</Button>
        <Button size="sm" variant="destructive" onClick={() => abrir("rechazado")}>Rechazar</Button>
        {app.status === "aprobado" && (
          <Button size="sm" variant="outline" onClick={() => abrir("suspendido")}>Suspender</Button>
        )}
      </div>

      <Dialog open={dialogo !== null} onOpenChange={(o) => !o && setDialogo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialogo === "aprobado" && "Aprobar solicitud"}
              {dialogo === "rechazado" && "Rechazar solicitud"}
              {dialogo === "suspendido" && "Suspender perfil"}
            </DialogTitle>
            <DialogDescription>
              {dialogo === "aprobado" &&
                `Al aprobar, el perfil de ${nombreCompleto} pasará a ser público y visible en el directorio.`}
              {dialogo === "rechazado" && "Un rechazo necesita un motivo. Se guardará en el historial de revisiones."}
              {dialogo === "suspendido" && "Indica el motivo de la suspensión. El perfil dejará de ser público."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`notas-${app.id}`}>{requiereMotivo ? "Motivo" : "Nota (opcional)"}</Label>
            <Textarea
              id={`notas-${app.id}`}
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              rows={4}
              placeholder={requiereMotivo ? "Explica el motivo de la decisión" : "Nota interna opcional"}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDialogo(null)} disabled={enviando}>
              Cancelar
            </Button>
            <Button onClick={confirmar} disabled={enviando || (requiereMotivo && !notas.trim())}>
              {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </article>
  );
};

const VerificationTab = ({
  estado,
  onEstadoChange,
  apps,
  loading,
  onDecision,
}: {
  estado: string;
  onEstadoChange: (v: string) => void;
  apps: Application[];
  loading: boolean;
  onDecision: (id: string, decision: string, notas: string) => Promise<boolean>;
}) => (
  <div className="space-y-6">
    <div className="max-w-xs space-y-2">
      <Label htmlFor="filtro-estado">Estado de la solicitud</Label>
      <Select value={estado} onValueChange={onEstadoChange}>
        <SelectTrigger id="filtro-estado">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ESTADOS.map((e) => (
            <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {loading ? (
      <div className="space-y-4">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-56 w-full rounded-2xl" />
        ))}
      </div>
    ) : apps.length === 0 ? (
      <p className="text-muted-foreground">
        {estado === "pendiente" ? "No hay solicitudes pendientes" : "No hay solicitudes con este estado"}
      </p>
    ) : (
      <div className="space-y-4">
        {apps.map((app) => (
          <ApplicationCard key={app.id} app={app} onDecision={onDecision} />
        ))}
      </div>
    )}
  </div>
);


type Metrics = {
  generado_en?: string;
  terapeutas?: {
    total?: number;
    reales?: number;
    demo?: number;
    publicos_reales?: number;
    por_estado?: Record<string, number>;
    altas_7d?: number;
    altas_30d?: number;
  };
  vistas?: {
    total?: number;
    ultimos_7d?: number;
    ultimos_30d?: number;
    perfiles_con_visitas?: number;
    top?: { id: string; nombre?: string | null; ciudad?: string | null; vistas?: number; es_demo?: boolean }[];
    contador_heredado_profiles_vistas?: number;
  };
  clientes?: { total?: number; altas_30d?: number; usuarios_auth?: number; perfiles_ritual?: number };
  actividad?: {
    reservas?: number;
    reservas_por_estado?: Record<string, number>;
    contactos?: number;
    contactos_sin_leer?: number;
    resenas?: number;
  };
  contenido?: {
    eventos?: number;
    eventos_por_estado?: Record<string, number>;
    sesiones_grupales?: number;
    sesiones_por_estado?: Record<string, number>;
  };
  revisiones?: { total?: number; ultimos_30d?: number; ultima?: string | null };
  series_altas?: { mes: string; terapeutas?: number; clientes?: number }[];
  notas?: string[];
};

const has = (v: unknown): v is number | string => v !== null && v !== undefined;

const BigStat = ({ label, value }: { label: string; value: number | string }) => (
  <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
    <p className="font-serif text-3xl text-foreground">{value}</p>
    <p className="mt-1 text-sm text-muted-foreground">{label}</p>
  </div>
);

const MetricBlock = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
    <h3 className="font-serif text-xl text-foreground">{title}</h3>
    <div className="mt-4 space-y-3 text-sm">{children}</div>
  </section>
);

const Dato = ({ label, value }: { label: string; value: number | string | null | undefined }) =>
  has(value) ? (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/60 pb-2 last:border-0 last:pb-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  ) : null;

const Etiquetas = ({ data }: { data?: Record<string, number> }) => {
  const entries = Object.entries(data ?? {});
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {entries.map(([k, v]) => (
        <Badge key={k} variant="secondary">
          {etiquetaTipo(k)}: {v}
        </Badge>
      ))}
    </div>
  );
};

const MetricsTab = () => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-dashboard-metrics"],
    queryFn: async (): Promise<Metrics> => {
      const { data, error } = await supabase.rpc("admin_dashboard_metrics");
      if (error) throw error;
      return (data ?? {}) as Metrics;
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-2xl" />
          ))}
        </div>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-40 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
        No se han podido cargar las métricas. {mensajeError(error)}
      </div>
    );
  }

  const m = data ?? {};
  if (Object.keys(m).length === 0) {
    return <p className="text-muted-foreground">No tienes permisos para ver las métricas.</p>;
  }

  const te = m.terapeutas ?? {};
  const vi = m.vistas ?? {};
  const cl = m.clientes ?? {};
  const ac = m.actividad ?? {};
  const co = m.contenido ?? {};
  const serie = m.series_altas ?? [];
  const maxSerie = Math.max(1, ...serie.map((s) => Math.max(s.terapeutas ?? 0, s.clientes ?? 0)));

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {has(te.reales) && <BigStat label="Terapeutas reales" value={te.reales!} />}
        {has(te.publicos_reales) && <BigStat label="Perfiles públicos reales" value={te.publicos_reales!} />}
        {has(cl.total) && <BigStat label="Clientes" value={cl.total!} />}
        {has(vi.ultimos_30d) && <BigStat label="Visitas últimos 30 días" value={vi.ultimos_30d!} />}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <MetricBlock title="Terapeutas">
          <Dato label="Total" value={te.total} />
          <Dato label="Reales" value={te.reales} />
          <Dato label="Demostración" value={te.demo} />
          <Dato label="Altas últimos 7 días" value={te.altas_7d} />
          <Dato label="Altas últimos 30 días" value={te.altas_30d} />
          <Etiquetas data={te.por_estado} />
        </MetricBlock>

        <MetricBlock title="Visitas a perfil">
          <Dato label="Total" value={vi.total} />
          <Dato label="Últimos 7 días" value={vi.ultimos_7d} />
          <Dato label="Últimos 30 días" value={vi.ultimos_30d} />
          <Dato label="Perfiles con visitas" value={vi.perfiles_con_visitas} />
          {vi.top && vi.top.length > 0 ? (
            <ul className="space-y-2 pt-2">
              {vi.top.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-secondary/40 px-3 py-2">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-foreground">{p.nombre ?? "Sin nombre"}</span>
                    {p.ciudad && <span className="text-muted-foreground">{p.ciudad}</span>}
                    {p.es_demo && <Badge variant="outline">DEMOSTRACIÓN</Badge>}
                  </span>
                  {has(p.vistas) && <span className="text-foreground">{p.vistas}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="pt-2 text-muted-foreground">Todavía no se ha registrado ninguna visita.</p>
          )}
        </MetricBlock>

        <MetricBlock title="Clientes y app">
          <Dato label="Clientes" value={cl.total} />
          <Dato label="Altas últimos 30 días" value={cl.altas_30d} />
          <Dato label="Cuentas totales" value={cl.usuarios_auth} />
          <Dato label="Perfiles del ritual" value={cl.perfiles_ritual} />
        </MetricBlock>

        <MetricBlock title="Actividad y contenido">
          <Dato label="Reservas" value={ac.reservas} />
          <Etiquetas data={ac.reservas_por_estado} />
          <Dato label="Contactos" value={ac.contactos} />
          <Dato label="Contactos sin leer" value={ac.contactos_sin_leer} />
          <Dato label="Reseñas" value={ac.resenas} />
          <Dato label="Eventos" value={co.eventos} />
          <Etiquetas data={co.eventos_por_estado} />
          <Dato label="Sesiones grupales" value={co.sesiones_grupales} />
          <Etiquetas data={co.sesiones_por_estado} />
        </MetricBlock>
      </div>

      {serie.length > 0 && (
        <MetricBlock title="Altas por mes">
          <div className="space-y-4">
            {serie.map((s) => (
              <div key={s.mes} className="space-y-1">
                <p className="text-sm text-muted-foreground">{s.mes}</p>
                <div className="flex items-center gap-2">
                  <span className="w-24 shrink-0 text-xs text-muted-foreground">Terapeutas</span>
                  <div className="h-2 flex-1 rounded-full bg-secondary">
                    <div className="h-2 rounded-full bg-primary" style={{ width: `${((s.terapeutas ?? 0) / maxSerie) * 100}%` }} />
                  </div>
                  <span className="w-8 shrink-0 text-right text-xs text-foreground">{s.terapeutas ?? 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-24 shrink-0 text-xs text-muted-foreground">Clientes</span>
                  <div className="h-2 flex-1 rounded-full bg-secondary">
                    <div className="h-2 rounded-full bg-accent" style={{ width: `${((s.clientes ?? 0) / maxSerie) * 100}%` }} />
                  </div>
                  <span className="w-8 shrink-0 text-right text-xs text-foreground">{s.clientes ?? 0}</span>
                </div>
              </div>
            ))}
          </div>
        </MetricBlock>
      )}

      {m.notas && m.notas.length > 0 && (
        <section className="rounded-2xl border border-border bg-secondary/30 p-5">
          <h3 className="font-serif text-lg text-foreground">Cómo leer estas cifras</h3>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
            {m.notas.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </section>
      )}

      {m.generado_en && (
        <p className="text-xs text-muted-foreground">Datos generados el {formatoFechaHora(m.generado_en)}</p>
      )}
    </div>
  );
};


const Admin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading, isAdmin, roleLoading } = useAuth();

  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [users, setUsers] = useState<UserProfileRow[]>([]);
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [query, setQuery] = useState("");

  const [estadoFiltro, setEstadoFiltro] = useState("pendiente");
  const [apps, setApps] = useState<Application[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [counts, setCounts] = useState<Record<string, number>>({});

  const loadCounts = useCallback(async () => {
    const { data, error } = await supabase.rpc("admin_therapist_application_counts");
    if (!error && data && typeof data === "object") setCounts(data as Record<string, number>);
  }, []);

  const loadApps = useCallback(async (status: string) => {
    setAppsLoading(true);
    const { data, error } = await supabase.rpc("admin_list_therapist_applications", { p_status: status });
    if (error) {
      toast.error(mensajeError(error));
      setApps([]);
    } else {
      setApps((data as Application[]) ?? []);
    }
    setAppsLoading(false);
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    void loadApps(estadoFiltro);
  }, [isAdmin, estadoFiltro, loadApps]);

  useEffect(() => {
    if (!isAdmin) return;
    void loadCounts();
  }, [isAdmin, loadCounts]);

  const handleDecision = useCallback(
    async (id: string, decision: string, notas: string) => {
      const { error } = await supabase.rpc("admin_review_therapist", {
        p_profile_id: id,
        p_decision: decision,
        p_notas: notas || undefined,
      });
      if (error) {
        toast.error(mensajeError(error));
        return false;
      }
      toast.success("Decisión guardada correctamente");
      await Promise.all([loadApps(estadoFiltro), loadCounts()]);
      return true;
    },
    [estadoFiltro, loadApps, loadCounts],
  );


  useEffect(() => {
    if (!loading && !user) navigate("/acceso", { replace: true, state: { from: "/admin" } });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    (async () => {
      setDataLoading(true);
      const [p, u, b] = await Promise.all([
        supabase.from("profiles").select("id,user_id,nombre,apellidos,ciudad,status,created_at").order("created_at", { ascending: false }).limit(500),
        supabase.from("user_profiles").select("id,user_id,display_name,phone,created_at").order("created_at", { ascending: false }).limit(500),
        supabase.from("bookings").select("id,client_name,client_email,appointment_date,appointment_time,status,amount_cents").order("created_at", { ascending: false }).limit(200),
      ]);
      if (!active) return;
      setProfiles((p.data as ProfileRow[]) ?? []);
      setUsers((u.data as UserProfileRow[]) ?? []);
      setBookings((b.data as BookingRow[]) ?? []);
      setDataLoading(false);
    })();
    return () => { active = false; };
  }, [isAdmin]);

  const filteredProfiles = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter((p) => `${p.nombre ?? ""} ${p.apellidos ?? ""} ${p.ciudad ?? ""}`.toLowerCase().includes(q));
  }, [profiles, query]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => `${u.display_name ?? ""} ${u.phone ?? ""}`.toLowerCase().includes(q));
  }, [users, query]);

  if (loading || roleLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
        <span className="sr-only">{t("common.loading")}</span>
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <>
        <SEO title={t("admin.deniedTitle")} description={t("admin.seoDescription")} noindex />
        <main className="flex min-h-screen items-center justify-center bg-background px-6 pt-24">
          <div className="max-w-md space-y-4 text-center">
            <h1 className="font-serif text-3xl text-foreground">{t("admin.deniedTitle")}</h1>
            <p className="text-muted-foreground">{t("admin.deniedText")}</p>
            <Button asChild>
              <Link to="/">{t("notFound.homeLabel")}</Link>
            </Button>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <SEO title={t("admin.seoTitle")} description={t("admin.seoDescription")} noindex />
      <main className="min-h-screen bg-background px-4 pb-20 pt-28 sm:px-6">
        <div className="mx-auto w-full max-w-6xl space-y-10">
          <header className="space-y-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs uppercase tracking-widest text-primary">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              {t("admin.badge")}
            </span>
            <h1 className="font-serif text-3xl text-foreground sm:text-4xl">{t("admin.title")}</h1>
            <p className="max-w-2xl text-muted-foreground">{t("admin.subtitle")}</p>
          </header>

          <section className="grid gap-4 sm:grid-cols-3" aria-label={t("admin.statsLabel")}>
            <StatCard icon={Sparkles} label={t("admin.statTherapists")} value={profiles.length} />
            <StatCard icon={Users} label={t("admin.statUsers")} value={users.length} />
            <StatCard icon={CalendarDays} label={t("admin.statBookings")} value={bookings.length} />
          </section>

          <div className="max-w-sm">
            <label htmlFor="admin-search" className="mb-2 block text-sm text-muted-foreground">
              {t("admin.searchLabel")}
            </label>
            <Input id="admin-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("admin.searchPlaceholder")} />
          </div>

          <Tabs defaultValue="verification" className="w-full">
              <TabsList className="flex-wrap">
                <TabsTrigger value="verification">
                  Verificación ({counts.pendiente ?? 0})
                </TabsTrigger>
                <TabsTrigger value="metrics">Métricas</TabsTrigger>
                <TabsTrigger value="therapists">{t("admin.tabTherapists")}</TabsTrigger>
                <TabsTrigger value="users">{t("admin.tabUsers")}</TabsTrigger>
                <TabsTrigger value="bookings">{t("admin.tabBookings")}</TabsTrigger>
              </TabsList>

              <TabsContent value="verification" className="mt-6">
                <VerificationTab
                  estado={estadoFiltro}
                  onEstadoChange={setEstadoFiltro}
                  apps={apps}
                  loading={appsLoading}
                  onDecision={handleDecision}
                />
              </TabsContent>

              <TabsContent value="metrics" className="mt-6">
                <MetricsTab />
              </TabsContent>


              <TabsContent value="therapists" className="mt-6 space-y-3">
                {dataLoading && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  {t("common.loading")}
                </div>
                )}
                {!dataLoading && filteredProfiles.length === 0 && <p className="text-muted-foreground">{t("admin.empty")}</p>}
                {filteredProfiles.map((p) => (
                  <article key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4 shadow-soft">
                    <div>
                      <p className="font-medium text-foreground">{[p.nombre, p.apellidos].filter(Boolean).join(" ") || t("admin.noName")}</p>
                      <p className="text-sm text-muted-foreground">{p.ciudad ?? t("admin.noCity")}</p>
                    </div>
                    <Badge variant="secondary">{p.status ?? t("admin.noStatus")}</Badge>
                  </article>
                ))}
              </TabsContent>

              <TabsContent value="users" className="mt-6 space-y-3">
                {dataLoading && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  {t("common.loading")}
                </div>
                )}
                {!dataLoading && filteredUsers.length === 0 && <p className="text-muted-foreground">{t("admin.empty")}</p>}
                {filteredUsers.map((u) => (
                  <article key={u.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4 shadow-soft">
                    <div>
                      <p className="font-medium text-foreground">{u.display_name || t("admin.noName")}</p>
                      <p className="text-sm text-muted-foreground">{u.phone ?? t("admin.noPhone")}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}
                    </span>
                  </article>
                ))}
              </TabsContent>

              <TabsContent value="bookings" className="mt-6 space-y-3">
                {dataLoading && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  {t("common.loading")}
                </div>
                )}
                {!dataLoading && bookings.length === 0 && <p className="text-muted-foreground">{t("admin.empty")}</p>}
                {bookings.map((b) => (
                  <article key={b.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4 shadow-soft">
                    <div>
                      <p className="font-medium text-foreground">{b.client_name || t("admin.noName")}</p>
                      <p className="text-sm text-muted-foreground">
                        {b.appointment_date ?? ""} {b.appointment_time ?? ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">
                        {b.amount_cents != null ? `${(b.amount_cents / 100).toFixed(2)} ${t("common.currency")}` : ""}
                      </span>
                      <Badge variant="secondary">{b.status ?? t("admin.noStatus")}</Badge>
                    </div>
                  </article>
                ))}
              </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Admin;
